import { useIntl } from 'react-intl';
import { getLocalDate } from '../utilities';
/**
 * Custom hook for handling localization using react-intl.
 * Returns an object with the `t` function for translating messages,
 * the `intl` object for accessing the react-intl API, the `locale` value, and some helper functions.
 */
export const useLocale = () => {
    const intl = useIntl();
    /**
     * Translates the given message ID using react-intl's `formatMessage` function.
     * @param id - The message ID to translate.
     * @param defaultMessage - The default message to use if no translation is found.
     * @param values - Optional values to be interpolated into the translated message.
     * @returns The translated message.
     */
    const t = (id, { defaultMessage, values } = {}) => intl.formatMessage({ id, defaultMessage }, values);
    /**
     * Formats the given amount as a currency string.
     *
     * @param amount - The amount to format.
     * @param currency - The currency code to use for formatting. Defaults to 'USD'.
     * @param showSymbol - Whether to include the currency symbol in the formatted string. Defaults to false.
     * @param onlySymbol - Whether to include the currency code in the formatted string. Defaults to false.
     * @returns The formatted currency string.
     */
    const formatCurrency = (amount, currency, showSymbol = false, onlySymbol = false) => {
        const resolvedCurrency = currency || 'USD';
        if (showSymbol) {
            const symbolText = intl.formatNumber(amount || 0, {
                currencyDisplay: 'narrowSymbol',
                style: 'currency',
                currency: resolvedCurrency,
                trailingZeroDisplay: 'stripIfInteger',
            });
            return onlySymbol ? symbolText : `${resolvedCurrency} ${symbolText}`;
        }
        return intl.formatNumber(amount || 0, {
            currencyDisplay: 'code',
            style: 'currency',
            currency: resolvedCurrency,
            trailingZeroDisplay: 'stripIfInteger',
        });
    };
    /**
     * Formats a decimal number with the specified precision.
     *
     * @param number - The number to format.
     * @param precision - The number of decimal places to include. Defaults to 2.
     * @returns The formatted decimal number.
     */
    const formatDecimal = (number, precision = 2) => {
        return intl.formatNumber(number, {
            style: 'decimal',
            minimumIntegerDigits: 1,
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        });
    };
    /**
     * Retrieves the symbol for the given currency.
     *
     * @param currency - The currency code.
     * @param includeCode - Whether to include the currency code in the symbol. Defaults to false.
     * @returns The symbol for the currency.
     */
    const getCurrencySymbol = (currency, includeCode = false) => {
        const parts = intl.formatNumberToParts(0, {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
        });
        const symbolPart = parts.find(part => part.type === 'currency');
        return `${includeCode ? `${currency} ` : ''}${symbolPart?.value}`;
    };
    const getTimeZoneName = () => {
        const parts = intl.formatDateToParts(new Date(), { timeZoneName: 'short' });
        const tz = parts.find(p => p.type === 'timeZoneName')?.value;
        return tz ? ` ${tz}` : '';
    };
    /**
     * Formats a date string to the local date format using the provided options.
     *
     * @param date - The datetime string to be formatted. This is assumed to be in UTC without the Z suffix.
     * @param options - The options for formatting the date.
     *                 For dateStyle, valid values are: 'full' | 'long' | 'medium' | 'short'
     *                   - full: "Thursday, April 20, 2023"
     *                   - long: "April 20, 2023"
     *                   - medium: "Apr 20, 2023"
     *                   - short: "4/20/23"
     *                 For timeStyle, valid values are: 'full' | 'long' | 'medium' | 'short'
     *                   - full: "1:45:30 PM Pacific Daylight Time"
     *                   - long: "1:45:30 PM PDT"
     *                   - medium: "1:45:30 PM"
     *                   - short: "1:45 PM"
     * @param defaultValue - The default value to be returned if the date is undefined.
     * @param showTimeZoneName - Whether to include the time zone name in the formatted string. Defaults to false.
     * @returns The formatted date string or the default value if the date is undefined.
     */
    const formatToLocalDatetime = (date, options, defaultValue = '-', showTimeZoneName = true) => {
        if (!date)
            return defaultValue;
        let timeZoneName = '';
        if (showTimeZoneName) {
            timeZoneName = getTimeZoneName();
        }
        return `${intl.formatDate(`${date}${date.endsWith('Z') ? '' : 'Z'}`, options)}${timeZoneName}`;
    };
    /**
     * Formats a date string to a localized date representation.
     *
     * @param date - The date string to be formatted. This is assumed to be just the date without any time
     * @param options - The options for formatting the date.
     *                 For dateStyle, valid values are: 'full' | 'long' | 'medium' | 'short'
     *                   - full: "Thursday, April 20, 2023"
     *                   - long: "April 20, 2023"
     *                   - medium: "Apr 20, 2023"
     *                   - short: "4/20/23"
     *                 Note: timeStyle is not typically used with this function as it's meant for date-only formatting
     * @param defaultValue - The default value to be returned if the date is undefined.
     * @returns The formatted localized date string.
     */
    const formatToLocalDate = (date, options, defaultValue = '-') => {
        if (!date)
            return defaultValue;
        return intl.formatDate(getLocalDate(date), options);
    };
    /**
     * Formats a date-only string (e.g., "2024-01-01") by interpreting it as UTC.
     * This prevents timezone conversion, ensuring the date displays exactly as stored.
     *
     * Use this for calendar dates that have no time component and should display
     * identically regardless of user timezone:
     *   - Benefit period start/end dates
     *   - Policy effective dates
     *   - Birthdates
     *   - Anniversary dates
     *
     * Do NOT use this for timestamps or datetime values with time components.
     * Use formatToLocalDatetime instead, which properly converts to the user's local timezone.
     *
     * Difference from formatToLocalDate:
     *   - formatToLocalDate: converts TO the user's local timezone (can cause date shifts)
     *   - formatDateAsUTC: interprets AS UTC (no conversion, date stays as-is)
     *
     * Example:
     *   Input: "2024-01-01"
     *   - formatDateAsUTC(date):    "Jan 1, 2024" (any timezone)
     *   - formatToLocalDate(date):  "Dec 31, 2023" (in PST/PDT due to timezone conversion)
     *
     * @param date - The date-only string to be formatted (e.g., "2024-01-01").
     * @param options - The options for formatting the date.
     *                 For dateStyle, valid values are: 'full' | 'long' | 'medium' | 'short'
     *                   - full: "Thursday, April 20, 2023"
     *                   - long: "April 20, 2023"
     *                   - medium: "Apr 20, 2023"
     *                   - short: "4/20/23"
     * @param defaultValue - The default value to be returned if the date is undefined.
     * @returns The formatted date string or the default value if the date is undefined.
     */
    const formatDateAsUTC = (date, options, defaultValue = '-') => {
        if (!date)
            return defaultValue;
        // Don't use getLocalDate() here - we want to avoid timezone conversion.
        // Ensure deterministic parsing for date-only strings by constructing a UTC midnight Date.
        const isoDateTime = date.includes('T') ? date : `${date}T00:00:00Z`;
        const parsedDate = new Date(isoDateTime);
        if (isNaN(parsedDate.getTime())) {
            return defaultValue;
        }
        return intl.formatDate(parsedDate, { ...options, timeZone: 'UTC' });
    };
    return {
        t,
        intl,
        locale: intl.locale,
        formatCurrency,
        formatDateAsUTC,
        formatToLocalDate,
        formatToLocalDatetime,
        getCurrencySymbol,
        formatDecimal,
    };
};
